import {
  EMPTY_GUID,
  isLayoutBlockType,
  type RepeatableColumn,
  type WorkflowField,
  type WorkflowTemplate,
} from '@/api/workflows';

import { type FieldValue } from './FieldRenderer';
import { type PdfFillMap } from './fillPdf';
import { getInstanceCount, qualifiedKey, sectionMinInstances } from './sectionInstances';

/**
 * Turn the advisor's runtime answer map into a Record keyed by AcroForm
 * field name for ONE target PDF (`pdfEntryId`), ready to feed straight into
 * `fillPdfWithValues`.
 *
 * Rules:
 *  - Walks every DynamicForm step in the template. PdfFileUpload +
 *    AdditionalDocuments etc. carry no fillable fields.
 *  - Skips layout blocks (StaticText, Paragraph, Divider, BulletedList,
 *    Notice, Image) — they don't hold advisor values.
 *  - A field maps to many AcroForm fields via `pdfMappings`, each scoped to a
 *    PDF entry; only mappings targeting `pdfEntryId` (or the legacy EMPTY_GUID
 *    "all documents" sentinel) emit here. Fields with no matching mapping are
 *    skipped.
 *  - Mapping is many-to-many: several form fields may target the SAME AcroForm
 *    field. They fill it waterfall-style, in the workflow order of this walk
 *    (step → section → row → column): each mapped field writes over the one
 *    before it, so the LAST mapped field in workflow order is what the advisor
 *    sees in the generated PDF.
 *  - RepeatableRow: its value is an array of row objects (one Record<colId,
 *    value> per row). Each column carries a per-row-index AcroForm handle
 *    (`col.mappings[rowIndex]`), so row R of column C lands in that column's
 *    handle for index R. Columns are document-agnostic (no PDF entry id), so
 *    they fill in every PDF. A legacy single `col.internalName` with a `{N}`
 *    token still resolves for backward compatibility.
 *  - Toggle / Checkbox → boolean; everything else → string. Per-option
 *    checkbox fills (below) stay document-agnostic — the same option-level
 *    AcroForm name is emitted for every PDF, unchanged.
 */
export function collectFilledFields(
  template: WorkflowTemplate,
  values: Record<string, FieldValue>,
  pdfEntryId: string,
): PdfFillMap {
  const out: PdfFillMap = {};

  for (const step of template.steps) {
    if (step.type !== 'DynamicForm') continue;
    for (const section of step.sections) {
      // §1 Repeatable Sections. Fields inside a repeatable section store
      // values under `inst:<sectionId>:<idx>:<fieldId>` keys. Every
      // current instance emits its own fill entry — the field's
      // (or option's) `internalName` may contain a `{n}` placeholder
      // that's substituted with the 1-based instance index so AH1 lands
      // in `firstname1`, AH2 in `firstname2`, etc. No placeholder ⇒ only
      // instance 0 emits (later instances would overwrite the same slot).
      const isRepeatable = section.repeatable === true;
      const instanceCount = isRepeatable
        ? getInstanceCount(values, section.id, sectionMinInstances(section))
        : 1;
      for (let i = 0; i < instanceCount; i++) {
        for (const row of section.rows) {
          for (const col of row.columns) {
            const field = col.field;
            if (!field) continue;
            const key = isRepeatable ? qualifiedKey(section.id, i, field.id) : field.id;
            collectFieldFills(field, values, out, key, i, isRepeatable, pdfEntryId);
            // Field-level text-slot mapping and per-option checkbox mappings
            // are independent — a Radio can legitimately carry BOTH (unlikely
            // for a single PDF, but the model doesn't forbid it).
            collectOptionFills(field, values, out, key, i, isRepeatable, pdfEntryId);
          }
        }
      }
    }
  }

  return out;
}

/**
 * Resolve the AcroForm name one mapping should fill for the current repeatable
 * instance (`instanceIdx`, 0-based), or null when it doesn't apply here.
 *
 *  - Non-repeatable field: the name is used as-is.
 *  - Per-index mapping (`mappingInstanceIndex` set, 1-based): the AcroForm name is
 *    concrete and belongs to exactly one instance — it fills only when its index
 *    matches the current instance, so instance N's value can't leak into instance
 *    M's field.
 *  - Legacy mapping (no index): a `{n}`/`{N}` token is substituted with the 1-based
 *    instance index; without a token it can only address one slot, so it fills only
 *    instance 0 (a later instance would overwrite the same slot).
 */
function resolveMappingName(
  rawName: string,
  mappingInstanceIndex: number | null | undefined,
  instanceIdx: number,
  isRepeatable: boolean,
): string | null {
  if (!isRepeatable) return rawName;
  if (mappingInstanceIndex != null) {
    return mappingInstanceIndex === instanceIdx + 1 ? rawName : null;
  }
  const hasPlaceholder = /\{[nN]\}/.test(rawName);
  const displayIdx = String(instanceIdx + 1);
  if (hasPlaceholder) return rawName.replace(/\{[nN]\}/g, displayIdx);
  return instanceIdx === 0 ? rawName : null;
}

function collectFieldFills(
  field: WorkflowField,
  values: Record<string, FieldValue>,
  out: PdfFillMap,
  valueKey: string,
  instanceIdx: number,
  isRepeatable: boolean,
  pdfEntryId: string,
) {
  if (isLayoutBlockType(field.type)) return;
  if (field.type === 'RepeatableRow') {
    collectRepeatableRowFills(field, values, out, valueKey);
    return;
  }
  const mappings = field.pdfMappings ?? [];
  if (mappings.length === 0) return;

  const raw = values[valueKey];
  if (raw === null || raw === undefined) return;

  // Coerce the advisor value once; every matching mapping lands the same value.
  let value: string | boolean;
  if (field.type === 'Toggle' || field.type === 'Checkbox') {
    value = raw === true;
  } else if (Array.isArray(raw)) {
    // CheckboxGroup / MultiSelect ship arrays; join with a comma for the
    // AcroForm text field. Objects (RepeatableRow rows) are gated out above.
    value = raw.map((v) => String(v)).join(', ');
  } else {
    value = String(raw);
  }

  for (const m of mappings) {
    // Only mappings for the PDF being filled (or the legacy "all documents"
    // sentinel) apply — this is what makes 1-to-many correct per-document.
    if (m.pdfEntryId !== pdfEntryId && m.pdfEntryId !== EMPTY_GUID) continue;
    const rawName = m.acroFieldName?.trim();
    if (!rawName) continue;
    const name = resolveMappingName(rawName, m.instanceIndex, instanceIdx, isRepeatable);
    if (name === null) continue;
    // Waterfall: a later field in workflow order mapped to this same box
    // overwrites what an earlier one wrote.
    out[name] = value;
  }
}

/**
 * Fill a RepeatableRow field: its value is an array of row objects keyed by
 * column id. Row R (1-based) of column C lands in that column's per-row-index
 * AcroForm handle. Columns are document-agnostic, so no PDF-entry filtering.
 */
function collectRepeatableRowFills(
  field: WorkflowField,
  values: Record<string, FieldValue>,
  out: PdfFillMap,
  valueKey: string,
) {
  const template = field.repeatableTemplate;
  if (!template || template.length === 0) return;
  const raw = values[valueKey];
  if (!Array.isArray(raw)) return;

  raw.forEach((row, idx) => {
    if (row === null || typeof row !== 'object' || Array.isArray(row)) return;
    const rowIndex = idx + 1; // 1-based, matches the visual mapping tool
    const cells = row as Record<string, unknown>;
    for (const col of template) {
      const name = resolveColumnAcroName(col, rowIndex);
      if (!name) continue;
      const cell = cells[col.id];
      if (cell === null || cell === undefined || cell === '') continue;
      out[name] = String(cell);
    }
  });
}

/**
 * Resolve the AcroForm field name for one column at a 1-based row index. Prefers
 * the per-row-index handle; falls back to a legacy single `internalName` (a `{N}`
 * token is substituted; without a token it can only address row 1).
 */
function resolveColumnAcroName(col: RepeatableColumn, rowIndex: number): string | null {
  const perRow = col.mappings?.find((m) => m.rowIndex === rowIndex)?.internalName?.trim();
  if (perRow) return perRow;
  const legacy = col.internalName?.trim();
  if (!legacy) return null;
  if (/\{[nN]\}/.test(legacy)) return legacy.replace(/\{[nN]\}/g, String(rowIndex));
  return rowIndex === 1 ? legacy : null;
}

/**
 * Walk the choice-type fields (Radio / SingleSelect / CheckboxGroup) and, for
 * each option the advisor has chosen (or ticked), check the option's mapped
 * AcroForm boolean field if one is authored. Complements the field-level
 * `internalName` handling above — that one fills a text-slot from the value;
 * this one flips a checkbox per option.
 */
function collectOptionFills(
  field: WorkflowField,
  values: Record<string, FieldValue>,
  out: PdfFillMap,
  valueKey: string,
  instanceIdx: number,
  isRepeatable: boolean,
  pdfEntryId: string,
) {
  if (
    field.type !== 'RadioGroup' &&
    field.type !== 'SingleSelect' &&
    field.type !== 'CheckboxGroup'
  ) {
    return;
  }
  const raw = values[valueKey];
  if (raw === null || raw === undefined) return;
  const picked = Array.isArray(raw) ? new Set(raw.map((v) => String(v))) : new Set([String(raw)]);
  for (const opt of field.options) {
    if (!picked.has(opt.value)) continue;
    // Each picked option can check boxes in many PDFs — only mappings targeting
    // the PDF being filled (or the legacy EMPTY_GUID sentinel) apply here.
    for (const m of opt.pdfMappings ?? []) {
      if (m.pdfEntryId !== pdfEntryId && m.pdfEntryId !== EMPTY_GUID) continue;
      const rawMapping = m.acroFieldName?.trim();
      if (!rawMapping) continue;
      const mapping = resolveMappingName(rawMapping, m.instanceIndex, instanceIdx, isRepeatable);
      if (mapping === null) continue;
      out[mapping] = true;
    }
  }
}
